import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Fade,
  Button,
  Tooltip,
  Paper,

} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AlertTable from './AlertTable';
import { apiService } from '../services/apiService';
import TinyMap from './TinyMap';

const AlertComponent = (props) => {
  const navigate = useNavigate();

  const [selectedRating, setSelectedRating] = useState(5);
  const [ratingCounts, setRatingCounts] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
  const [companyType, setCompanyType] = useState('all'); // Track selected company type
  const [companyOffers, setCompanyOffers] = useState([]);
  const [totalOfferCount, setTotalOfferCount] = useState(0)

  const sentimentFiles = [
    { rating: 5, emoji: "😎", label: "Excellent" },
    { rating: 4, emoji: "😀", label: "Very Good" },
    { rating: 3, emoji: "🙂", label: "Good" },
    { rating: 2, emoji: "😐", label: "Poor" },
    { rating: 1, emoji: "😡", label: "Very Poor" },
  ];

  // Fetch Feedback Stats
  useEffect(() => {
    const fetchStats = async () => {
      const CACHE_KEY = 'feedbackStatsCache';
      const CACHE_EXPIRY = 24 * 60 * 60 * 1000;

      try {
        const cachedStr = localStorage.getItem(CACHE_KEY);
        if (cachedStr) {
          const cached = JSON.parse(cachedStr);
          const now = Date.now();
          if (now - cached.timestamp < CACHE_EXPIRY) {
            setRatingCounts(cached.counts);
            return;
          }
        }

        const data = await apiService.fetchFeedbackStats(props.filters);
        const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        if (data?.feedback_stats) {
          data.feedback_stats.forEach(item => {
            if (item.rating >= 1 && item.rating <= 5) {
              counts[item.rating] = item.count || 0;
            }
          });
        }
        setRatingCounts(counts);

        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          filters: props.filters,
          counts: counts
        }));

      } catch (error) {
        console.error("Error fetching feedback stats:", error);
      }
    };

    const fetchCompanyOffers = async () => {
      const CACHE_KEY = 'companyOffersCache';
      const CACHE_EXPIRY = 24 * 60 * 60 * 1000;

      try {
        const cachedStr = localStorage.getItem(CACHE_KEY);
        if (cachedStr) {
          const cached = JSON.parse(cachedStr);
          const now = Date.now();
          if (now - cached.timestamp < CACHE_EXPIRY) {
            setTotalOfferCount(cached.totalOfferCount);
            setCompanyOffers(cached.companyOffers);
            return;
          }
        }

        const res = await apiService.fetchCompanyOffers(props.filters);
        const data = res?.data || [];
        const total = data.reduce((acc, item) => acc + (item.total || 0), 0);
        setTotalOfferCount(total);
        setCompanyOffers(data);

        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          filters: props.filters,
          totalOfferCount: total,
          companyOffers: data
        }));
      } catch (error) {
        console.error("Error fetching company offers:", error);
      }
    }

    fetchStats();
    fetchCompanyOffers();
  }, []);

  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
    navigate('/feedback', { state: { rating, autoFetch: true } });
  };

  const handleCompanyTypeChange = (type) => {
    setCompanyType(type);
  };

  const getCompanyData = () => {
    if (Array.isArray(props.companyData)) {
      // Legacy flat array format — return as is
      return props.companyData;
    }
    if (props.companyData && typeof props.companyData === 'object') {
      if (companyType === 'all') {
        // Merge both lists into one flat array
        const conv = props.companyData['converged'] || [];
        const nonConv = props.companyData['non_converged'] || [];
        return [...conv, ...nonConv];
      }
      return props.companyData[companyType] || [];
    }
    return [];
  };

  // Convert stateData array to object format for TinyMap
  const convertStateDataToObject = (stateDataArray) => {
    if (!Array.isArray(stateDataArray)) return {};

    return stateDataArray.reduce((acc, item) => {
      if (item.key && item.doc_count !== undefined) {
        acc[item.key] = item.doc_count;
      }
      return acc;
    }, {});
  };

  const stateDataObject = convertStateDataToObject(props.stateData);


  return (
    <Fade in timeout={500}>
      <Box sx={{ p: { xs: 1, sm: 1.5, md: 2, lg: 0, xl: 0 }, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>

        {/* Feedback and Complaints Row Container */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 2
        }}>
          <Box sx={{
            flex: 1,
            border: '1px solid',
            borderColor: '#B0BEC5',
            borderRadius: 2,
            py: 2,
            px: 1,
            paddingBottom: 1,
            paddingTop: 1,
            position: 'relative',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#3B32CB',
              boxShadow: '0 4px 12px rgba(59, 50, 203, 0.1)'
            }
          }}>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: -10,
                left: 16,
                bgcolor: '#ECEAFC',
                px: 1,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 1,

              }}
            >
              Complaints
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 0 }}>
              {[
                {
                  title: "Total Complaints",
                  valueKey: "totalCount",
                  path: "/complaints",
                  // bg: "#9B86FF",
                  bg: "#c6e1ff",
                  shadow: "rgba(157, 236, 252, 0.2)",
                  // hoverShadow: "rgba(250, 67, 84, 0.3)",
                  color: "#333",
                  textColor: "#333",
                },
                {
                  title: "Total Categories",
                  valueKey: "categoriesCount",
                  path: "/categories",
                  bg: "#f5dad6ff",
                  shadow: "rgba(224, 157, 103, 0.2)",
                  // hoverShadow: "rgba(255, 152, 0, 0.3)",
                  color: "#333",
                  textColor: "#333",
                },
                {
                  title: "High Alerts",
                  valueKey: "highAlerts",
                  path: "/alert",
                  // bg: "#2B9EF0",
                  // bg: "#9B86FF",
                  bg: "#D0CEFB",

                  shadow: "rgba(158, 128, 197, 0.2)",
                  // hoverShadow: "rgba(244, 67, 54, 0.3)",
                  color: "#333",
                  textColor: "#333",
                },
              ].map((item, index) => (
                <Card
                  key={index}
                  onClick={() => navigate(item.path)}
                  sx={{
                    width: '100%',
                    p: { xs: 1, sm: 1.5 },
                    px: { xs: 1.5, sm: 2 },
                    height: { xs: 45, sm: 50, md: 50, lg: 50, xl: 70 },
                    background: item.bg,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.5, md: 2, xl: 1.5 },
                    boxShadow: `0 2px 16px ${item.shadow}`,
                    transition: "0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      // boxShadow: `0 4px 20px ${item.hoverShadow}`,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: item.color,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem", lg: "0.85rem", xl: "1rem" },

                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: item.textColor,
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem", lg: "1.2rem", xl: "1.3rem" },
                      ml: 'auto',

                    }}
                  >
                    {item.valueKey === "totalCount"
                      ? props.totalCount
                      : item.valueKey === "categoriesCount"
                        ? props.categoriesCount
                        : props.highAlertCount}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>
          <Box sx={{
            flex: 1,
            border: '1px solid',
            borderColor: '#B0BEC5',
            borderRadius: 2,
            py: 2,
            px: 1,
            paddingBottom: 1,
            paddingTop: 1,
            position: 'relative',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#3B32CB',
              boxShadow: '0 4px 12px rgba(59, 50, 203, 0.1)'
            }
          }}>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: -10,
                left: 16,
                bgcolor: '#ECEAFC',
                px: 1,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 1,

              }}
            >
              Feedback
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {sentimentFiles.map(({ rating, emoji, label }) => {
                const isSelected = selectedRating === rating;
                const count = ratingCounts[rating] || 0;

                return (
                  <Tooltip key={rating} arrow placement="top">
                    <Button
                      onClick={() => handleRatingClick(rating)}
                      variant="text"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        py: 1,
                        px: 1.5,
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        borderRadius: 2,
                        backgroundColor: "#D0CEFB",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, lineHeight: 1, }}>
                          {emoji}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: "#333",
                            fontWeight: 500,
                            textTransform: 'none',

                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          fontWeight: 500,
                          color: "#333",

                        }}
                      >
                        {count}
                      </Typography>
                    </Button>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        </Box>


        {/* Tables Section */}
        <Box sx={{
          flex: 1,
          height: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gridTemplateRows: { xs: 'auto auto auto auto', sm: '1fr 1fr' },
          gap: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 },
          px: { xs: 0.5, sm: 1 },

        }}>
          <Box sx={{ minHeight: { xs: 280, sm: 250, md: 250, lg: 250, xl: 240 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AlertTable title="High Alerts" data={props.alertData} loading={props.alertsLoading !== undefined ? props.alertsLoading : props.loading} />
          </Box>
          <Box sx={{ minHeight: { xs: 280, sm: 250, md: 250, lg: 250, xl: 240 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AlertTable title="Categories" data={props.categoryData} loading={props.categoriesLoading !== undefined ? props.categoriesLoading : props.loading} />
          </Box>
          <Box sx={{ minHeight: { xs: 280, sm: 250, md: 250, lg: 250, xl: 240 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AlertTable
              title="Companies"
              data={getCompanyData()}
              loading={props.companiesLoading !== undefined ? props.companiesLoading : props.loading}
              onCompanyTypeChange={handleCompanyTypeChange}
            />
          </Box>
          {/* Tiny Map for States */}
          <Paper
            elevation={3}
            sx={{
              minHeight: { xs: 280, sm: 250, md: 250, lg: 250, xl: 200 },
              height: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {(props.statesLoading !== undefined ? props.statesLoading : props.loading) && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #3498db',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} />
              </Box>
            )}
            {props.stateData && <TinyMap data={stateDataObject} title="State Distribution" />}
          </Paper>

          <Box sx={{ gridColumn: '1 / -1', minHeight: { xs: 280, sm: 250, md: 250, lg: 250, xl: 240 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AlertTable
              title={`Company offers (${totalOfferCount})`}
              data={companyOffers}
              loading={props.companiesLoading !== undefined ? props.companiesLoading : props.loading}
            // onCompanyTypeChange={handleCompanyTypeChange}
            />
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default AlertComponent;